import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, Inject, Optional, signal, Signal, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA  } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { CacheService } from '@app/_services/cache.service';
import { JournalStore } from '@app/_stores';
import { Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { AlertService } from '@app/_services/alert.service';
import { UtilsService } from '@app/_services/utils.service';
import { CollaboratorService } from '@app/_services/collaborator.service';
import { BlockedUserService } from '@app/_services/blocked-user.service';
import { getTotalCollaboratorsCount, getRoleByUid } from '@app/_utils/members-model.utils';
import { MatMenuModule } from '@angular/material/menu';
import { CollaboratorComponent } from '@app/components/addons/collaborators/collaborator/collaborator.component';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { InputHashtagComponent } from '@app/components/shared/inputs/input-hashtag/input-hashtag.component';
import { HashtagComponent } from '@app/components/addons/hashtags/hashtag/hashtag.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { InputDatePeriodComponent } from '@app/components/shared/inputs/input-date-period/input-date-period.component';
import { InputTimeComponent } from '@app/components/shared/inputs/input-time/input-time.component';
import { getCustomDate } from '@app/_services/date';
import { InputReminderComponent } from '@app/components/shared/inputs/input-reminder/input-reminder.component';
import { notOnlyWhitespace } from '@app/_directives/form-validator.directive';
import { CollaboraterRole, CollaboraterStatus } from '@app/_types/generic.type';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { CommentsListComponent } from '@app/components/addons/comments/comments-list/comments-list.component';
// JournalEntriesComponent loaded dynamically to break circular dependency
import { log } from '@app/_services/logger';
import { CommonSetupTypeComponent } from '@app/components/shared/common-setup-type/common-setup-type.component';
import { FeatureGuideDialogComponent } from '@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component';
import { ModalService } from '@app/_services/modal.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { MenuManagerService } from '@app/_services/menu-manager.service';
import { JOURNAL_SETUP_MENU } from '@app/_configs/menu-configs/common-menu.config';
import { MenuAction } from '@app/_interfaces/menu-config.interface';

@Component({
  selector: 'app-journal-setup-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    InputTextComponent,
    InputHashtagComponent,
    InputDatePeriodComponent,
    SvgComponent,
    InputTimeComponent,
    InputReminderComponent,
    CommonSetupTypeComponent
  ],
  templateUrl: './journal-setup-form.component.html',
  styleUrl: './journal-setup-form.component.scss'
})

export class JournalSetupFormComponent implements OnInit, OnDestroy {

  @ViewChild('jSetupForm') jSetupForm!: NgForm;
  unSubscribe = new Subject<void>();
  journalSetupForm: FormGroup;
  journalSetupInitial: JournalSetup;
  readonly journalStore = inject(JournalStore);
  readonly menuManager = inject(MenuManagerService);
  private readonly menuConfig = JOURNAL_SETUP_MENU;
  submitted: boolean = false;
  mode: WritableSignal<'new' | 'edit'> = signal('new');
  membersVersion: WritableSignal<number> = signal(0); // Track members changes for reactivity
  dataSetupId: WritableSignal<string> = signal('');
  setup: Signal<JournalSetup> = computed(() => {
    const setupId = this.dataSetupId();
    return this.journalStore.idToSetup()[setupId] || {} as JournalSetup;
  });
  collabCount: Signal<number> = computed(() => {
    // For new entities, read from form control (updates live as collaborators are added)
    // For existing entities, read from store (which reflects saved state)
    this.membersVersion(); // Subscribe to members changes for reactivity
    if (this.mode() === 'new') {
      const formMembers = this.journalSetupForm?.get('members')?.value;
      if (!formMembers) return 0;
      return getTotalCollaboratorsCount(formMembers);
    }
    const m = this.setup().members;
    if (!m) return 0;
    return getTotalCollaboratorsCount(m);
  });
  isCollab: Signal<boolean> = computed(() => {
    return this.collabCount() > 0;
  });

  // Show collaborate button for: new entities (owner can add collaborators) OR existing entities with collaborators
  showCollaborateBtn: Signal<boolean> = computed(() => {
    return this.mode() === 'new' || this.isCollab();
  });

  role: Signal<CollaboraterRole> = computed(() => {
    if (this.mode() === 'new') {
      return 'owner';
    } else if (this.setup().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.setup().members) {
      return getRoleByUid(this.setup().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });

  deleted: Signal<boolean> = computed(() => {
    if (this.mode() === 'new') return false;
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return false;
    return true;
  });

  // Check if the entity owner is blocked (for Block/Unblock button)
  isOwnerBlocked: Signal<boolean> = computed(() => {
    const ownerEmail = this.setup().members?.ownerEmail;
    if (!ownerEmail) return false;
    return this.blockedUserService.isBlocked(ownerEmail);
  });

  deletedAtString: Signal<string> = computed(() => {
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return '';
    return this.ss.interpolateText('common_deletedAt', { date: this.ss.getFormattedDate(deletedAt, 'd MMM yyyy'), time: this.ss.getFormattedDate(deletedAt, 'h:mm a') });
  });

  featureStatus = computed(() => {
    const setup = this.setup();
    if (this.mode() === 'new' || !setup.startAt || !setup.endAt) return 'active';
    return this.ds.getFeatureStatus(setup.startAt.dateString, setup.endAt?.dateString);
  });

  screenEnterTime: Signal<Date> = signal(new Date());
  repeatDates: Signal<string[]> = computed(() => {
    const setup = this.setup();
    if (!setup || this.mode() === 'new' || setup?.repeat?.length === 0) return [];
    return this.ds.getAllDatesOfEntity(setup, 'journal', this.cc.user.uid);
  });

  // Menu system signals
  topBarActions = computed(() => this.menuManager.getTopBarActions(this.menuConfig, this));
  topBarActionsWithoutSpecial = computed(() =>
    this.topBarActions().filter(action => action.id !== 'close' && action.id !== 'kebab')
  );
  kebabAction = computed(() => this.topBarActions().find(action => action.id === 'kebab'));
  kebabMenuActions = computed(() =>
    this.menuManager.getKebabMenuActions(this.menuConfig, this.topBarActions(), this)
  );

  public data: { mode: 'new' | 'edit', value: JournalSetup };

  constructor(
    @Optional() public dialogRef: MatDialogRef<JournalSetupFormComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<JournalSetupFormComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { mode: 'new' | 'edit', value: JournalSetup } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { mode: 'new' | 'edit', value: JournalSetup } | null,
    private fb: FormBuilder,
    public cc: CacheService,
    private alertService: AlertService,
    private utilsService: UtilsService,
    public dialog: MatDialog,
    public ds: DependencyService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private collaboratorService: CollaboratorService,
    private blockedUserService: BlockedUserService,
    private router: Router
  ) {
    this.data = (dialogData || bottomSheetData)!;

    this.mode.set(this.data.mode);
    this.dataSetupId.set(this.data?.value?.id || '');
    log.debug('constructor', 'Journal setup form data', { value: this.data.value });
    this.journalSetupInitial = this.data.mode == 'new' ? this.initiateForm() : this.initiateForm(this.data.value);

    this.journalSetupForm = this.fb.group({
      id: new FormControl(this.journalSetupInitial.id, Validators.required),
      title: new FormControl(this.journalSetupInitial.title, [Validators.required, Validators.maxLength(200), notOnlyWhitespace()]),
      duration: new FormControl(this.journalSetupInitial.duration),
      isStartTimeSet: new FormControl(this.journalSetupInitial.isStartTimeSet),
      isTzAffected: new FormControl(this.journalSetupInitial.isTzAffected),
      reminderAt: new FormControl(this.journalSetupInitial.reminderAt),
      repeat: new FormControl(this.journalSetupInitial.repeat),
      startAt: new FormControl(this.journalSetupInitial.startAt),
      endAt: new FormControl(this.journalSetupInitial.endAt),
      tags: new FormControl(this.journalSetupInitial.tags),
      uid: new FormControl(this.journalSetupInitial.uid, Validators.required),
      members: new FormControl(this.journalSetupInitial.members),
    });

    dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });

    bottomSheetRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  ngOnInit() {
    const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
    this.as.screenViewEvent('journalSetup', previousScreen);
    if (this.mode() === 'edit') {
      this.as.crudEvent('read', 'journalSetup', this.cc.user.uid === this.setup().uid ? 'private' : 'shared', this.setup().members?.memberUids.length || 0, 'notShared', this.role(), 'none');
    }
  }

  initiateForm(journalSetup?: JournalSetup): JournalSetup {
    return {
      id: journalSetup ? journalSetup.id : this.utilsService.getNewId(),
      title: journalSetup ? journalSetup.title : '',
      duration: journalSetup ? journalSetup.duration : 15,
      isStartTimeSet: journalSetup ? journalSetup.isStartTimeSet : false,
      isTzAffected: journalSetup ? journalSetup.isTzAffected : false,
      reminderAt: journalSetup ? journalSetup.reminderAt : [],
      repeat: journalSetup ? journalSetup.repeat : ["RRULE:FREQ=DAILY"],
      startAt: journalSetup ? journalSetup.startAt : getCustomDate(),
      endAt: journalSetup ? journalSetup.endAt : null,
      tags: journalSetup ? journalSetup.tags : [],
      uid: journalSetup ? journalSetup.uid : this.cc.user.uid,
      members: journalSetup ? journalSetup.members : { ownerEmail: this.cc.user.email, memberUids: [], collaborators: {} },
    }
  }

  cloneSetup() {
    const setupData = { ...this.setup() };
    setupData.id = this.utilsService.getNewId();
    setupData.title = this.ss.interpolateText('journalSetupAdd_cloneSetupTitleCopy', { journalTitle: setupData.title });
    const customDate = getCustomDate();
    customDate.timeString = setupData.startAt.timeString;
    setupData.startAt = customDate;
    setupData.endAt = null;
    setupData.members = { ownerEmail: this.cc.user.email, memberUids: [], collaborators: {} };

    this.journalSetupForm.get('id')?.setValue(setupData.id);
    this.journalSetupForm.get('title')?.setValue(setupData.title);
    this.journalSetupForm.get('startAt')?.setValue(setupData.startAt);
    this.journalSetupForm.get('endAt')?.setValue(setupData.endAt);
    this.journalSetupForm.get('members')?.setValue(setupData.members);
    this.journalSetupInitial = setupData;

    this.mode.set('new');
    this.dataSetupId.set('');
  }

  getFc(fcName: string): FormControl {
    return this.journalSetupForm.get(fcName) as FormControl;
  }

  hasChanges() {
    const initial = _.cloneDeep(this.journalSetupInitial);
    const current = _.cloneDeep(this.journalSetupForm.value);
    return !_.isEqual(initial, current);
  }

  async openEntities() {
    // Dynamic import to break circular dependency:
    // journal-setup-form -> journal-entries -> journal-block -> journal-form -> journal-setup-form
    const { JournalEntriesComponent } = await import('../journal-entries/journal-entries.component');
    this.modalService.openLevel2(
      JournalEntriesComponent,
      { entityId: this.dataSetupId() },
      { disableClose: false, autoFocus: false }
    );
  }

  addCollaborator() {
    const entityData = this.mode() === 'new'
      ? this.buildCurrentSetupFromForm()
      : this.setup();

    const dialogRef = this.modalService.openLevel2(
      CollaboratorComponent,
      {
        collection: 'journalSetups',
        entityData: entityData,
        isPublic: false,
        isNewEntity: this.mode() === 'new',
        onMembersUpdatedForNewEntity: this.mode() === 'new'
          ? (updatedMembers: any) => {
              this.journalSetupForm.get('members')?.setValue(updatedMembers);
              // Also update journalSetupInitial for proper change detection
              this.journalSetupInitial = { ...this.journalSetupInitial, members: updatedMembers };
              // Trigger collabCount reactivity
              this.membersVersion.set(this.membersVersion() + 1);
            }
          : undefined,
      },
      { disableClose: true }
    );

    // For edit mode: increment membersVersion after dialog closes to trigger reactivity
    if (this.mode() === 'edit') {
      this.modalService.getClosedObservable(dialogRef).pipe(takeUntil(this.unSubscribe)).subscribe(() => {
        // Increment to trigger collabCount re-computation (reads from updated store)
        this.membersVersion.set(this.membersVersion() + 1);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Build current setup from form data (for new entities being created)
   */
  private buildCurrentSetupFromForm(): JournalSetup {
    const formValue = this.journalSetupForm.value;
    return {
      ...formValue,
      members: formValue.members || {
        ownerEmail: this.cc.user.email,
        memberUids: [],
        members: {},
        pendingInvites: {}
      }
    } as JournalSetup;
  }

  /**
   * Execute action from menu configuration
   * Called from template when menu items are clicked
   */
  executeAction(action: MenuAction): void {
    let methodName = action.action;

    // Handle special cases
    if (methodName === 'leaveSetup') {
      this.removeCollab('left');
      return;
    }
    if (methodName === 'blockSetup') {
      this.removeCollab('blocked');
      return;
    }

    // Standard action execution
    const self = this as Record<string, any>;
    if (typeof self[methodName] === 'function') {
      self[methodName]();
    } else {
      console.log(`JournalSetupFormComponent: Action method '${methodName}' not found`);
    }
  }

  /**
   * Get badge value for a menu action
   * Called from template to display badges
   */
  getBadgeValue(action: MenuAction): string | number | null {
    return this.menuManager.getBadgeValue(action, this);
  }

  /**
   * Check if kebab menu should be displayed
   */
  showKebabMenu(): boolean {
    return this.menuManager.shouldShowKebab(this.kebabMenuActions());
  }

  /**
   * Get dynamic block/unblock label
   */
  getBlockLabel(): string {
    return this.isOwnerBlocked() ? 'common_unblock' : 'common_block';
  }

  /**
   * Get collaborator icon based on whether there are collaborators
   */
  getCollaboratorIcon(): string {
    return this.isCollab() ? 'users' : 'addUser';
  }

  async removeCollab(action: CollaboraterStatus) {
    if (action === 'left') {
      const res = await this.alertService.confirm(this.ss.texts()['leaveJournal_title'], this.ss.texts()['leaveJournal_content'], this.ss.texts()['common_leave'], this.ss.texts()['common_buttonCancel'], 'color-11', 'color-7');
      if (!res?.confirm) return;
      try {
        this.cc.isLoading.set(true);
        const success = await this.collaboratorService.removeMemberFromEntity(
          this.setup().id, 'journalSetups', this.cc.user.email
        );
        if (!success) throw new Error('Failed to remove member from entity');
        this.close();
      } catch (error) {
        log.error('removeCollab', 'Failed to leave journal', error as Error);
        this.alertService.alert(this.ss.texts()['common_error'], this.ss.texts()['journal_leave_error']);
      } finally {
        this.cc.isLoading.set(false);
      }
    } else if (action === 'blocked') {
      const ownerEmail = this.setup().members?.ownerEmail;
      if (!ownerEmail) return;
      await this.blockedUserService.toggleBlockWithConfirmation(ownerEmail, 'journalSetups');
    }
  }

  openHashtagsDialog() {
    const dialog = this.modalService.openLevel2(HashtagComponent, {
      values: this.journalSetupForm.value.tags,
      type: 'map'
    }, {
      disableClose: true
    })

    this.modalService.getClosedObservable(dialog).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result) {
        this.journalSetupForm.get('tags')?.setValue(result);
      }
      this.cdr.markForCheck();
    });
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.texts()['saveChanges_title'], this.ss.texts()['common_discardContent'], this.ss.texts()['common_save'], this.ss.texts()['common_discard'], 'color-35', 'color-11');
      if (!res) return;
      if (res.confirm === true) {
        this.save();
      } else if (res.confirm === false) {
        this.close();
      }
    } else {
      this.close();
    }
  }

  save() {
    this.submitted = true;
    if (this.journalSetupForm.invalid) {
      this.alertService.alert(this.ss.texts()['common_mandatoryAlertTitle'], this.ss.texts()['common_titleMandatory'], this.ss.texts()['common_ok']);
      return;
    };

    if (this.mode() === 'new') {
      this.journalStore.addJournalSetup(this.journalSetupForm.value);
    } else if (this.mode() === 'edit') {
      const updatedSetup = { ...this.setup(), ...this.journalSetupForm.value };
      this.journalStore.updateJournalSetups([updatedSetup]);
    }
    this.close({ mode: this.mode(), id: this.journalSetupForm.value.id });
    this.router.navigate(['/features']);
  }

  async deleteJournalSetup() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.texts()['journalSetupEditDelete_title'],
      description: this.ss.texts()['journalSetupEditDelete_content'],
      yesButtonText: this.ss.texts()['common_delete'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    this.journalStore.deleteJournalSetup([this.setup()]);
    this.close();
  }

  openFeatureGuide() {
    const setupDialog = this.modalService.openLevel1(
      FeatureGuideDialogComponent,
      {
        entityType: 'journal'
      },
      { disableClose: true, autoFocus: false }
    );
  }

  openComments() {
    const commentsDialog = this.modalService.openLevel2(
      CommentsListComponent,
      {
        entityId: this.setup().id,
        collection: FirestoreCollection.JournalSetups,
        members: this.setup().members,
        entityUid: this.setup().uid || this.cc.user.uid,
        newEntity: null
      },
      { disableClose: true, autoFocus: false }
    );
  }

  async remove() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.texts()['removeConfirmation_title'],
      description: this.ss.texts()['removeConfirmation_content'],
      yesButtonText: this.ss.texts()['common_buttonRemove'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.setup();
    journal.permaDeletedAt = new Date();
    this.journalStore.updateJournalSetups([journal]);
  };

  async restore() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.texts()['restoreConfirmation_title'],
      description: this.ss.texts()['restoreConfirmation_content'],
      yesButtonText: this.ss.texts()['common_restore'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    const journal = this.setup();
    journal.deletedAt = null;
    this.journalStore.updateJournalSetups([journal]);
  };

  ngOnDestroy() {
    this.as.screenExitEvent('journalSetup', this.screenEnterTime());
    this.unSubscribe?.complete();
    this.unSubscribe?.next();
  }
}
